import { createColumnHelper, Row } from "@tanstack/react-table";
import { format } from "date-fns/format";
import React from "react";
import { Link } from "react-router-dom";

import { filterDateColumn } from "../../components/Table/filters/DateFilter";
import {
  convertUTCToLocalTime,
  joinClassNames,
  toTitleCase,
} from "../../shared/Functions";

export const columnHelper = createColumnHelper();

const displayMovementCount = (
  movement: string,
  index: number,
  isDropdown: boolean,
) => {
  const [movementType, movementCount] = movement.split(":");
  return (
    <div
      className={joinClassNames(
        isDropdown ? " justify-between" : "justify-begin",
        "flex items-center",
      )}
      data-testid="movement"
      key={index}
    >
      <span className="font-medium py-1 pr-1" data-testid="movement-type">
        {movementType}:
      </span>
      <p className="text-gray-900 font-medium" data-testid="movement-count">
        {movementCount}
      </p>
    </div>
  );
};

export const SharedSessionColumns = (isPlayerDetails?: boolean): any[] => {
  return [
    columnHelper.accessor("tableValues.sessionIdColumn", {
      id: "sessionIdColumn",
      header: "Session ID",
      enableSorting: false,
      size: 300,
      minSize: 200,
      cell: function idCell(element: any) {
        const columnValue = element.getValue();
        const [sessionId, mocapTypeName] = columnValue.split(", ");
        const rebootMotionSessionId = element.row.original.id;
        return (
          <div
            className={joinClassNames(
              isPlayerDetails ? "2xl:w-64" : "lg:w-64 xl:w-72 2xl:w-96",
              "w-52 overflow-hidden pr-6 flex flex-col",
            )}
          >
            <Link
              to={`/session/${rebootMotionSessionId}`}
              className="font-bold text-primary-700 hover:underline block truncate hover:text-clip hover:whitespace-normal hover:break-all"
              data-testid="session-link"
            >
              {sessionId}
            </Link>
            <p
              className="text-sm text-gray-500 pt-1"
              data-testid="session-mocap-type"
            >
              {mocapTypeName}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("sessionDate", {
      header: "Session Date",
      sortingFn: "datetime",
      filterFn: filterDateColumn,
      size: 200,
      minSize: 150,
      cell: function dateCell(element: any) {
        return (
          <div data-testid="session-date">
            {format(new Date(element.getValue()), "M/d/yyyy")}
          </div>
        );
      },
    }),
    columnHelper.accessor("typeAndVenue", {
      header: "Session Label",
      sortingFn: (rowA: Row<any>, rowB: Row<any>) => {
        const typeA = rowA.original.typeAndVenue.split(", ")[0];
        const typeB = rowB.original.typeAndVenue.split(", ")[0];
        if (typeA > typeB) {
          return 1;
        }
        if (typeB > typeA) {
          return -1;
        }
        return 0;
      },
      sortDescFirst: false,
      size: 200,
      cell: function typeAndVenueCell(element: any) {
        const value = element.getValue();
        const typeAndVenue = value.split(", ");
        const sessionType = typeAndVenue ? typeAndVenue[0] : "unknown";
        const sessionVenue = typeAndVenue ? typeAndVenue[1] : "unknown";
        return (
          <div className="flex flex-col">
            <div
              className="flex justify-begin items-center"
              data-testid="session-type"
            >
              <span className="text-gray-900 font-medium py-1" id={sessionType}>
                {toTitleCase(sessionType)}
              </span>
            </div>
            <div
              className="flex justify-begin items-center"
              data-testid="session-venue"
            >
              <span className="font-medium">
                {toTitleCase(
                  sessionVenue === "default" ? "Default Venue" : sessionVenue,
                )}
              </span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("tableValues.numberOfMovements", {
      header: "# of Movements",
      enableSorting: false,
      size: 250,
      cell: function numberOfMovementsCell(element: any) {
        const movements =
          element.getValue() !== ""
            ? element.getValue().split(", ")
            : undefined;
        const sessionTypeSlug = element.row.original.sessionType?.slug;
        return (
          <div className="flex-col flex" data-testid="number-of-movements">
            {movements &&
              (movements.length > 2 ? (
                <div className="relative group">
                  <button
                    className="font-medium border border-gray-300 bg-white rounded-md shadow-sm px-2 py-1"
                    data-testid="movement-dropdown-button"
                  >
                    {displayMovementCount(movements[0], 0, false)}
                  </button>
                  <div
                    className="absolute hidden group-hover:block bg-white border border-gray-300 shadow-lg p-2 z-10 rounded-md min-w-[200px] max-w-[250]"
                    data-testid="movement-dropdown-list"
                  >
                    {movements.map((movement: string, index: number) => {
                      if (index === 0) {
                        return null;
                      }
                      return displayMovementCount(movement, index - 1, true);
                    })}
                  </div>
                </div>
              ) : sessionTypeSlug === "movement-screen" &&
                movements.length === 2 ? (
                displayMovementCount(movements[1], 1, false)
              ) : (
                movements.map((movement: string, index: number) => {
                  return displayMovementCount(movement, index, false);
                })
              ))}
          </div>
        );
      },
    }),
    columnHelper.accessor("createdAt", {
      header: "Date Created",
      sortingFn: "datetime",
      size: 200,
      minSize: 150,
      cell: function dateCell(element: any) {
        return (
          <div className="font-medium text-gray-900" data-testid="upload-date">
            {convertUTCToLocalTime(element.getValue())}
          </div>
        );
      },
    }),
    columnHelper.accessor("statusComponent", {
      header: "Status",
      enableSorting: false,
      size: 200,
      cell: (info) => info.getValue(),
    }),
  ];
};
